import routes from "@config/routes";
import { Home as HomeIcon } from "@mui/icons-material";
import { Box, Grid, IconButton, MenuItem, Select, Snackbar, Stack, Typography, useTheme } from "@mui/material";
import { setSidebarFilter } from "@store/filters/actions";
import { setCurrentVisualization } from "@store/options/actions";
import { VisTypes } from "@store/options/types";
import { WranglerItem } from "fileParsing/wrangler/types";
import { useAppDispatch, useAppSelector } from "index";
import React, { ReactNode, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useNavigate, useParams } from "react-router-dom";
import FileSelector from "../WranglerView/FileSelector";
import MapView from "../WranglerView/MapView";

function WranglerPage() {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const providerItems = useAppSelector((s) => s.data.exports);

  const [selectedFile, setSelectedFile] = useState<WranglerItem | undefined>();
  const [message, setMessage] = useState<React.ReactElement | undefined>();

  const { archiveId } = useParams();

  const selectedArchive = providerItems.find(({ id }) => String(id) === archiveId);

  React.useEffect(() => {
    dispatch(setSidebarFilter([]));
    dispatch(setCurrentVisualization(VisTypes.All));
  }, []);

  const renderSelectedFile = () => {
    if (selectedFile === undefined) {
      return (
        <Stack justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
          <Typography variant="h4">Select a file on the left</Typography>
        </Stack>
      );
    }
    // Status should be the same for each element in current
    return (
      <>
        <Typography variant="h4">{selectedFile.name}</Typography>
        <Box sx={{ position: "absolute", top: 50, bottom: 0, width: "100%" }}>
          <MapView selection={selectedFile} />
        </Box>
      </>
    );
  };

  const renderSelectedArchive: () => ReactNode = () => {
    return (
      <Grid container>
        <Grid item xs={3}>
          <div style={{ height: "100vh" }}>
            <Stack
              direction="row"
              sx={{
                position: "relative",
                background: theme.palette.background.default,
              }}
            >
              <IconButton onClick={() => navigate(routes.Application)}>
                <HomeIcon />
              </IconButton>
              <Select
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "0px",
                    borderRadius: 0,
                  },
                }}
                onChange={(ev) => {
                  navigate(routes.Application + "/wrangler/" + ev.target.value);
                  setSelectedFile(undefined);
                }}
                value={selectedArchive?.id}
                renderValue={(value) => (
                  <Typography fontWeight="bold" align="center">
                    {providerItems.find(({ id }) => value === id)?.fileProperties.fileName ?? "- Select Archive -"}
                  </Typography>
                )}
              >
                {providerItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.fileProperties.fileName}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <div style={{ height: "calc(100vh - 62px)" }}>
              <FileSelector
                exportId={selectedArchive?.id}
                workerId={selectedArchive?.workerId}
                selected={selectedFile}
                onChange={setSelectedFile}
                onConfirm={() => {
                  setSelectedFile(undefined);
                }}
              />
            </div>
          </div>
        </Grid>
        <Grid
          item
          xs={9}
          sx={{
            width: "120%",
            position: "relative",
          }}
        >
          {renderSelectedFile()}
        </Grid>
      </Grid>
    );
  };

  return (
    <DndProvider backend={HTML5Backend}>
      {selectedArchive !== undefined ? renderSelectedArchive() : null}
      <Snackbar open={message !== undefined} onClose={() => setMessage(undefined)} autoHideDuration={6000}>
        {message}
      </Snackbar>
    </DndProvider>
  );
}

export { WranglerPage };
